import { ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import styles from './mini-task-detail.module.scss';
import { blue, charcoal } from '@/ui/styles';
import {
    UIButton,
    UIErrorState,
    UIIcon,
    UILoadingState,
    UIMenu,
    UIMenuItem,
    UIPopover,
    UIPropoverRef,
    UIText,
    UITextButton
} from '@/ui';
import { TaskBeaconsList } from './task-beacons-list';
import { TaskDetailMap } from './task-detail-map';
import { TaskEventsList } from './task-events-list';
import { TaskHeader } from './task-header';
import { TaskLastLocationList } from './task-last-location-list';
import { Link } from 'react-router';
import { Task } from '@/api';
import { useEnableTaskSockets, useFetchTask } from '../queries';
import {
    TaskBeaconSnapshotProvider,
    TaskLocationsProvider,
    useTaskBeaconMarkers,
    useTaskBeaconSnapshot,
    useTaskLocations,
    useTaskUserMarker
} from '../providers';
import { useCompanyContext } from '@/companies/providers';
import { useFormatDate } from '@/common/hooks';
import { useTasksTranslation } from '../hooks';

type MiniTaskDetailProps = {
    taskId: string | null;
};

export function MiniTaskDetail(props: MiniTaskDetailProps): ReactElement {
    const { taskId } = props;

    const companyId = useCompanyContext().company.id;

    return (
        <div className={styles.container}>
            {taskId ? (
                <TaskLocationsProvider taskId={taskId} companyId={companyId}>
                    <FilledMiniTaskDetail taskId={taskId} />
                </TaskLocationsProvider>
            ) : (
                <EmptyMiniTaskDetail />
            )}
        </div>
    );
}

function EmptyMiniTaskDetail(): ReactElement {
    const { t } = useTasksTranslation();

    return (
        <div className={styles.emptyContainer}>
            <UIIcon name="clipboard" size={48} color={charcoal} />
            <UIText variant="h2">
                {t('components.mini_task_detail.empty_title')}
            </UIText>
            <UIText>
                {t('components.mini_task_detail.empty_description')}
            </UIText>
        </div>
    );
}

function FilledMiniTaskDetail(props: { taskId: string }): ReactElement {
    const { taskId } = props;

    const { t } = useTasksTranslation();
    const { status, task, refetch } = useFetchTask(taskId);

    return (
        <div className={styles.filledContainer}>
            {status === 'pending' && <UILoadingState />}
            {status === 'error' && (
                <UIErrorState
                    title={t('components.mini_task_detail.load_error')}
                    onRetry={refetch}
                />
            )}
            {status === 'success' && task && (
                <TaskBeaconSnapshotProvider task={task}>
                    <TaskLoaded task={task} />
                </TaskBeaconSnapshotProvider>
            )}
        </div>
    );
}

function TaskLoaded(props: { task: Task }): ReactElement {
    const { task } = props;

    useEnableTaskSockets(task.id, task.companyId);

    return (
        <>
            <SelectedTaskMap task={task} />
            <SelectedTaskInfo task={task} />
        </>
    );
}

const base = 132; // px (40 + 16 + 76)
const step = 92; // px (16 + 76)
const targetFraction = 1 / 3; // 33%

function SelectedTaskMap(props: { task: Task }): ReactElement {
    const { task } = props;

    const userMarker = useTaskUserMarker(task);
    const { locations, status } = useTaskLocations();
    const beaconMarkers = useTaskBeaconMarkers(task);

    const [height, setHeight] = useState(base);

    useEffect(() => {
        const updateHeight = () => {
            const targetHeight = window.innerHeight * targetFraction;
            const steps = Math.floor((targetHeight - base) / step);
            const finalHeight = base + Math.max(0, steps) * step;
            setHeight(finalHeight);
        };

        updateHeight(); // run once on mount
        window.addEventListener('resize', updateHeight);
        return () => window.removeEventListener('resize', updateHeight);
    }, []);

    return (
        <div className={styles.mapContainer} style={{ height: `${height}px` }}>
            <TaskDetailMap
                taskId={task.id}
                taskStatus={
                    task.simpleStatus === 'COMPLETED' ? 'closed' : 'open'
                }
                route={locations}
                userImage={task.displayInfo.image}
                user={userMarker}
                beacons={beaconMarkers}
                showControls={false}
                shouldAnimate={false}
                status={status}
            />
        </div>
    );
}

function SelectedTaskInfo(props: { task: Task }): ReactElement {
    const { task } = props;
    const { t } = useTasksTranslation();
    const { formatSmartShortTimeAndDate } = useFormatDate();

    const ref = useRef<UIPropoverRef>(null);

    const [selectedIndex, setSelectedIndex] = useState(0);
    // Rebuilt each render rather than translated once at module scope, so it
    // follows a language change.
    const buttons = useMemo(
        () => [
            t('shared.tasks.section_events'),
            t('shared.tasks.section_last_known_location'),
            t('shared.tasks.section_nearby_beacons')
        ],
        [t]
    );
    const buttonText = buttons[selectedIndex];

    function onClick(index: number) {
        setSelectedIndex(index);
        ref.current?.close();
    }

    const { latestLocation } = useTaskLocations();
    const { updatedAtStr } = useTaskBeaconSnapshot();

    const timeString = useMemo(() => {
        if (selectedIndex === 1) {
            if (latestLocation) {
                return t('shared.tasks.updated_at', {
                    time: formatSmartShortTimeAndDate(
                        new Date(latestLocation.ts)
                    )
                });
            }
        } else if (selectedIndex === 2) {
            if (updatedAtStr) {
                return t('shared.tasks.updated_at', { time: updatedAtStr });
            }
        }
        return '';
    }, [
        formatSmartShortTimeAndDate,
        latestLocation,
        updatedAtStr,
        selectedIndex,
        t
    ]);

    return (
        <div className={styles.taskInfoContainer}>
            <div className={styles.taskInfoHeaderContainer}>
                <TaskHeader task={task} />
            </div>
            <div className={styles.taskInfoContentContainer}>
                <div className={styles.taskEventListContainer}>
                    <div className={styles.sectionHeaderWrapper}>
                        <UIPopover
                            ref={ref}
                            placement="bottom_right"
                            popoverPadding={4}
                            trigger="click"
                            triggerNode={
                                <button className={styles.removeButtonStyle}>
                                    <UIButton
                                        variant="textPrimary"
                                        rightIcon="chevron-down"
                                        withoutButtonHtmlTag
                                        customPadding={8}
                                    >
                                        {buttonText}
                                    </UIButton>
                                </button>
                            }
                        >
                            <UIMenu>
                                {buttons.map((button, index) => (
                                    <UIMenuItem
                                        key={index}
                                        text={button}
                                        onClick={() => {
                                            onClick(index);
                                        }}
                                        leftIconName={
                                            selectedIndex === index
                                                ? 'check'
                                                : undefined
                                        }
                                        leftIconColor={blue}
                                    />
                                ))}
                            </UIMenu>
                        </UIPopover>

                        <UIText type="default2" variant="small">
                            {timeString}
                        </UIText>
                    </div>
                    {selectedIndex === 0 && <TaskEventsList task={task} />}
                    {selectedIndex === 1 && (
                        <TaskLastLocationList
                            onViewAllBeacons={() => {
                                setSelectedIndex(2);
                            }}
                        />
                    )}
                    {selectedIndex === 2 && <TaskBeaconsList />}
                </div>
            </div>

            <div className={styles.taskInfoFooterContainer}>
                <Link
                    to={`/company/${task.companyId}/tasks/${task.id}`}
                    style={{ textDecoration: 'none' }}
                >
                    <UITextButton variant="primary">
                        {t('components.mini_task_detail.open_full_view')}
                    </UITextButton>
                </Link>
            </div>
        </div>
    );
}
